import React from "react";
import { useTranslation } from "react-i18next";
import { Volume2, VolumeX } from "lucide-react";
import { Button } from "../../ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../ui/tooltip";
import { useAudioNotifications } from "../../../hooks/useAudioNotifications";

/**
 * AudioNotificationToggle - Toggle button for enabling/disabling audio notifications
 *
 * Displays a speaker icon that changes based on enabled state:
 * - Volume2: Audio enabled
 * - VolumeX: Audio disabled
 *
 * Clicking toggles the state and plays a test sound when enabling.
 */
export const AudioNotificationToggle: React.FC = () => {
  const { t } = useTranslation();
  const { settings, toggle, playTestSound } = useAudioNotifications();

  const handleClick = () => {
    // If we're enabling audio, play a test sound
    if (!settings.enabled) {
      toggle();
      // Small delay to ensure state updates before playing
      setTimeout(() => {
        playTestSound();
      }, 100);
    } else {
      toggle();
    }
  };

  const tooltipText = settings.enabled
    ? t("admin_common_audio_notifications_enabled", "Audio notifications on")
    : t("admin_common_audio_notifications_disabled", "Audio notifications off");

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClick}
            className={settings.enabled ? "text-primary" : "text-muted-foreground"}
          >
            {settings.enabled ? (
              <Volume2 className="h-[1.2rem] w-[1.2rem]" />
            ) : (
              <VolumeX className="h-[1.2rem] w-[1.2rem]" />
            )}
            <span className="sr-only">{tooltipText}</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltipText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default AudioNotificationToggle;
